import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';

import Analytics from '@module/analytics';

export default class AudioPlayer extends React.Component {
  static propTypes = {
    file: PropTypes.object,
    metadata: PropTypes.object,
  };

  state = {
    canBePlayed: false,
    currentTime: 0,
    duration: 0,
    playing: false,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.canBePlayed = this.canBePlayed.bind(this);
    this.ended = this.ended.bind(this);
    this.loading = this.loading.bind(this);
    this.playPause = this.playPause.bind(this);
    this.renderBufferBar = this.renderBufferBar.bind(this);
    this.seek = this.seek.bind(this);
    this.updateProgress = this.updateProgress.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.file !== this.props.file) {
      this.setState({ playing: false });
    }
  }

  /**
   * Register all necessary listeners for audio events
   */
  componentDidMount() {
    if (!this.audioNode) {
      return;
    }

    // Avoid CORS issues
    this.audioNode.crossOrigin = 'anonymous';
    // Audio source is compatible and can be played
    this.audioNode.addEventListener('canplay', this.canBePlayed);
    // Because we all love Safari
    this.audioNode.addEventListener('loadedmetadata', this.canBePlayed);
    // Playback has stopped because of a temporary lack of data, like buffering
    this.audioNode.addEventListener('waiting', this.loading);
    // Playing, update progress, reproduction times, etc
    this.audioNode.addEventListener('timeupdate', this.updateProgress);
    // Audio finish playing. Reset state
    this.audioNode.addEventListener('ended', this.ended);
    // Audio is buffering
    this.audioNode.addEventListener('progress', this.renderBufferBar);
    this.audioNode.addEventListener('seeked', this.renderBufferBar);

    // D&D for pin element
    const self = this;
    window.addEventListener('mousedown', function (event) {
      if (event.target !== self.pinNode) {
        return;
      }

      const seekMethod = self.seek.bind(self);
      self.sliderNode.classList.add('audio-player__slider--no-animation');
      this.addEventListener('mousemove', seekMethod, false);
      window.addEventListener(
        'mouseup',
        () => {
          if (
            self.sliderNode &&
            self.sliderNode.classList.contains(
              'audio-player__slider--no-animation'
            )
          ) {
            self.sliderNode.classList.remove(
              'audio-player__slider--no-animation'
            );
          }
          window.removeEventListener('mousemove', seekMethod, false);
        },
        false
      );
    });
  }

  /**
   * Stop playing audio when the component it's going to be removed
   */
  componentWillUnmount() {
    this.audioNode.pause();
    this.audioNode.removeEventListener('canplay', this.canBePlayed);
    this.audioNode.removeEventListener('ended', this.ended);
    this.audioNode.removeEventListener('loadedmetadata', this.canBePlayed);
    this.audioNode.removeEventListener('progress', this.renderBufferBar);
    this.audioNode.removeEventListener('seeked', this.renderBufferBar);
    this.audioNode.removeEventListener('timeupdate', this.updateProgress);
    this.audioNode.removeEventListener('waiting', this.loading);
  }

  /**
   * The audio file has been buffered and it's compatible with the browser.
   * We can play it! Render audio information like duration and ID3 tags
   */
  canBePlayed() {
    if (!this.audioNode) {
      return;
    }

    // Special thanks to Firefox <= 55 where duration can be Inf
    const duration = isNaN(this.audioNode.duration)
      ? 0
      : this.audioNode.duration;

    this.setState({ canBePlayed: true, duration });
    this.renderBufferBar();
  }

  /**
   * Buffering the audio...
   */
  loading() {
    this.setState({ canBePlayed: false });
  }

  /**
   * End of the audio reproduction
   */
  ended() {
    this.setState({ playing: false });
  }

  /**
   * Play/pause the audio.
   * If it's the end the file, start from the beginning again.
   */
  playPause() {
    if (this.state.playing) {
      this.audioNode.pause();
    } else {
      // Start playing again from the begining
      if (this.audioNode.currentTime === this.audioNode.duration) {
        this.audioNode.currentTime = 0;
      }
      this.audioNode.play();

      Analytics.trackSnowplowEvent({
        category: 'preview',
        action: 'click_audio_start',
      });
    }

    this.setState({ playing: !this.state.playing });
  }

  /**
   * Format given seconds into m:ss, mm:ss or h:mm:ss
   * @param  {Number} seconds Number of seconds to format
   * @return {String}         Formated seconds
   */
  formatTime(seconds = 0) {
    const time = new Date(seconds * 1000).toUTCString();
    if (seconds >= 3600) {
      // Include hours: 0:00:00
      return time.substr(time.length - 11, 8);
    } else if (seconds >= 600) {
      // Display minutes and seconds: 00:00
      return time.substr(time.length - 9, 5);
    }

    // Display minute and seconds: 0:00
    return time.substr(time.length - 8, 5);
  }

  /**
   * Seek reproduction to a certain point
   * @param  {Event} event User click event
   */
  seek(event) {
    /*
     Magic operations to determine the position where the user wants to seek the audio
     */
    const controlPosition = this.sliderNode.getBoundingClientRect();
    const clickedPosition = event.pageX - controlPosition.left;
    const sliderWidth = this.sliderNode.clientWidth;

    let width =
      ((this.audioNode.duration * (clickedPosition / sliderWidth)) /
        this.audioNode.duration) *
      100;
    width = width > 100 ? 100 : width;
    this.progressNode.style.width = width + '%';
    this.audioNode.currentTime =
      this.audioNode.duration * (clickedPosition / sliderWidth);
    this.setState({ currentTime: this.audioNode.currentTime });
  }

  /**
   * Update the progress bar and pin position
   */
  updateProgress() {
    const percent =
      (this.audioNode.currentTime /
        (this.audioNode.duration || Number.MAX_SAFE_INTEGER)) *
      100;
    this.progressNode.style.width = percent + '%';
    this.setState({ currentTime: this.audioNode.currentTime });
  }

  /**
   * Render buffer bar
   */
  renderBufferBar() {
    if (!this.audioNode) {
      return;
    }

    const duration = this.audioNode.duration;
    if (duration > 0) {
      for (let i = 0; i < this.audioNode.buffered.length; i++) {
        // More than one portions of the file can be buffered. Take the "first" one.
        if (
          this.audioNode.buffered.start(
            this.audioNode.buffered.length - 1 - i
          ) <= this.audioNode.currentTime
        ) {
          const percent =
            this.audioNode.buffered.end(
              this.audioNode.buffered.length - 1 - i
            ) / duration;
          this.bufferNode.style.width = percent * 100 + '%';
          break;
        }
      }
    }
  }

  /**
   * Render main action for player, meaning loading, play or pause button
   */
  renderMainAction() {
    const buttonWrapper = (path) => (
      <div className="audio-player__playpause" onClick={this.playPause}>
        <svg width="30px" height="30px" viewBox="0 0 30 30">
          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g transform="translate(-20.000000, -35.000000)">
              <g transform="translate(20.000000, 35.000000)">
                <g>
                  <circle cx="15" cy="15" r="15" />
                  {path}
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
    );
    const playButton = buttonWrapper(
      <path d="M12,10.8685171 L12,19.1314829 L12,19.1314829 C12,19.6837677 12.4477153,20.1314829 13,20.1314829 C13.197425,20.1314829 13.3904327,20.0730449 13.5547002,19.9635332 L19.7519246,15.8320503 L19.7519246,15.8320503 C20.2114532,15.5256978 20.3356271,14.9048285 20.0292747,14.4452998 C19.9560398,14.3354475 19.8617768,14.2411846 19.7519246,14.1679497 L13.5547002,10.0364668 L13.5547002,10.0364668 C13.0951715,9.73011434 12.4743022,9.85428821 12.1679497,10.3138169 C12.058438,10.4780844 12,10.6710921 12,10.8685171 Z" />
    );
    const pauseButton = buttonWrapper(
      <path d="M10 10.496C10 9.67 10.666 9 11.5 9c.828 0 1.5.68 1.5 1.496v9.008C13 20.33 12.334 21 11.5 21c-.828 0-1.5-.68-1.5-1.496v-9.008zm7 0C17 9.67 17.666 9 18.5 9c.828 0 1.5.68 1.5 1.496v9.008C20 20.33 19.334 21 18.5 21c-.828 0-1.5-.68-1.5-1.496v-9.008z" />
    );

    return this.state.playing ? pauseButton : playButton;
  }

  render() {
    const {
      file: { previewUri },
    } = this.props;
    const { currentTime, duration } = { ...this.state };
    const time =
      currentTime > 0
        ? this.formatTime(currentTime)
        : this.formatTime(duration);
    const playerClasses = ['audio-player__controls'];
    if (Browser.isMobile) {
      playerClasses.push('audio-player__controls--left');
    }

    return (
      <div className="audio-player">
        <audio
          ref={(node) => (this.audioNode = node)}
          preload="metadata"
          src={previewUri}
        />
        <div className={playerClasses.join(' ')}>{this.renderMainAction()}</div>
        <div
          className={
            playerClasses.join(' ') + ' audio-player__controls--slider'
          }
        >
          <div
            className="audio-player__slider"
            ref={(node) => (this.sliderNode = node)}
            onClick={this.seek}
          >
            <div className="audio-player__slider--background">
              <div
                className="audio-player__slider--buffer"
                ref={(node) => (this.bufferNode = node)}
              />
              <div
                className="audio-player__slider--progress"
                ref={(node) => (this.progressNode = node)}
              >
                <div
                  className="audio-player__slider--pin"
                  ref={(node) => (this.pinNode = node)}
                />
              </div>
            </div>
          </div>
          <div className="audio-player__time audio-player__time--right">
            {time}
          </div>
        </div>
      </div>
    );
  }
}
